import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AppConfigService } from 'src/app/core/app-config/app-config.service';
import { CsvRowKeys, groupCsvRowKeys, groupCsvDisplayMap } from '../constants/csv.constants';
import { AllowedTranslationKey } from '../constants/allowed-translation-key';
@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private readonly translatorMap = {
    currentLang: new Map<string, string>(),
    en: new Map<string, string>()
  };

  constructor(
    public translateService: TranslateService,
    private readonly appConfigService: AppConfigService
  ) {}

  public async getEnglishTranslation(translationValue: string): Promise<string> {
    const cachedTranslation = Array.from(this.translatorMap.en.entries()).find(([value]) => {
      return value.trim().toLowerCase() === translationValue.trim().toLowerCase();
    });
    if (cachedTranslation) {
      return cachedTranslation[1];
    }
    const translationKey = this.findTranslationKeyByValue(this.translatorMap.currentLang, translationValue);

    if (translationKey) {
      const englishTranslation = this.translatorMap.en.get(translationKey);
      if (englishTranslation) {
        this.translatorMap.en.set(translationValue, englishTranslation);
        return englishTranslation;
      } else {
        this.handleErrorMessage(translationValue);
        return '';
      }
    } else {
      this.handleErrorMessage(translationValue);
      return '';
    }
  }

  public async getUserLanguageTranslation(englishTranslation: string): Promise<string> {
    const cachedTranslation = Array.from(this.translatorMap.currentLang.entries()).find(([value]) => {
      return value.trim().toLowerCase() === englishTranslation.trim().toLowerCase();
    });
    if (cachedTranslation) {
      return cachedTranslation[1];
    }

    const translationKey = this.findTranslationKeyByValue(this.translatorMap.en, englishTranslation);

    if (translationKey) {
      const userTranslation = this.translatorMap.currentLang.get(translationKey);
      return userTranslation && userTranslation !== translationKey ? userTranslation : englishTranslation;
    } else {
      return englishTranslation;
    }
  }

  private findTranslationKeyByValue(translations: Map<string, string>, value: string): string | null {
    const lowerCaseValue = value.trim().toLowerCase();
    for (const [key, translatedValue] of translations.entries()) {
      if (translatedValue.trim().toLowerCase() === lowerCaseValue && AllowedTranslationKey.includes(key)) {
        return key;
      }
    }
    return null;
  }

  public async loadTranslationsForLanguage(lang: string): Promise<void> {
    if (lang === this.appConfigService.configuration.defaultLanguage) {
      this.translateService.use(this.appConfigService.configuration.defaultLanguage).subscribe((translations: any) => {
        for (const [key, value] of Object.entries(translations)) {
          this.translatorMap.en.set(key, value as string);
        }
      });
    } else {
      this.translateService.use(lang).subscribe((translations: any) => {
        for (const [key, value] of Object.entries(translations)) {
          this.translatorMap.currentLang.set(key, value as string);
        }
      });
    }
  }
  public getCachedTranslation(key: string, lang: string): string | undefined {
    return lang === this.appConfigService.configuration.defaultLanguage
      ? this.translatorMap.en.get(key)
      : this.translatorMap.currentLang.get(key);
  }
  public handleErrorMessage(value: string): boolean {
    console.log(`Translation not found ${value}`);
    throw new Error('TranslationNotFound');
  }

  private async translateHeaders(headers: string[], currentLang: string): Promise<string[]> {
    const mappedHeaders = headers.map((header) => groupCsvDisplayMap[header] ?? header);

    if (currentLang !== this.appConfigService.configuration.defaultLanguage) {
      return Promise.all(
        mappedHeaders.map(async (header) => {
          return this.getUserLanguageTranslation(header);
        })
      );
    }

    return mappedHeaders;
  }

  public async getTranslatedHeaders(currentLang: string): Promise<string[]> {
    return this.translateHeaders(CsvRowKeys, currentLang);
  }

  public async getGroupTranslatedHeaders(currentLang: string): Promise<string[]> {
    return this.translateHeaders(groupCsvRowKeys, currentLang);
  }

  public checkMissingInCurrentLang(value: string): boolean | '' {
    const normalizedValue = value.trim().toLowerCase();
    for (const [, translatedValue] of this.translatorMap.currentLang.entries()) {
      if (translatedValue.trim().toLowerCase() === normalizedValue) {
        return true;
      }
    }
    for (const [, translatedValue] of this.translatorMap.en.entries()) {
      if (translatedValue.trim().toLowerCase() === normalizedValue) {
        return false;
      }
    }
    this.handleErrorMessage(value);
    return '';
  }
}
